import React, { useState, useEffect } from 'react';
import { Megaphone, PlusCircle, Edit, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const emptyForm = {
  name: '', ad_type: 'text', video_url: '', text_content: '',
  display_style: 'ticker', schedule_start: '', schedule_end: '', is_active: true, channel_ids: [], target_mode: 'all', viewer_ids: [], side: 'right', image_url: '', bottom_image_url: '', at_launch: true, on_channel_change: false, every_minutes: '', min_gap_minutes: '10'
};

const toLocalInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + 'T' + p(d.getHours()) + ':' + p(d.getMinutes());
};
const toPayload = (f) => ({
  ...f,
  schedule_start: f.schedule_start ? new Date(f.schedule_start).toISOString() : '',
  schedule_end: f.schedule_end ? new Date(f.schedule_end).toISOString() : '',
});

const Ads = () => {
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editAd, setEditAd] = useState(null);
  const [uploading, setUploading] = useState('');
  const uploadMedia = async (kind, file, field) => {
    if (!file) return;
    setUploading('Uploading ' + file.name + '...');
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('file', file);
      const r = await api.post('/api/uploads/ad-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = window.location.origin.replace('admin.', 'api.') + r.data.url;
      setForm(f => ({ ...f, [field]: url }));
      setUploading('');
      toast.success(kind === 'video' ? ('Video uploaded (' + r.data.seconds + ' s, ' + r.data.size_mb + ' MB)') : 'Image uploaded');
      if (r.data.warning) toast(r.data.warning, { duration: 8000 });
    } catch (err) {
      setUploading('');
      toast.error(err.response?.data?.message || 'Upload failed');
    }
  };
  const [form, setForm] = useState(emptyForm);
  const [channels, setChannels] = useState([]);
  const [chSearch, setChSearch] = useState('');
  const [vSearch, setVSearch] = useState('');
  const [viewerMacs, setViewerMacs] = useState({});
  const [endUsers, setEndUsers] = useState([]);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [targetAd, setTargetAd] = useState(null);
  const [targetMode, setTargetMode] = useState('all');
  const [targetVideo, setTargetVideo] = useState({ on_channel_change: false, every_minutes: '', min_gap_minutes: '10' });
  const [selectedViewerIds, setSelectedViewerIds] = useState([]);

  const fetchAds = async () => {
    try {
      const res = await api.get('/api/ads');
      setAds(res.data.data || []);
      try {
        const chRes = await api.get(user?.role === 'superadmin' ? '/api/channels' : '/api/channels/order/distributor');
        setChannels((chRes.data.data || []).filter(c => !c.is_adult));
      } catch { setChannels([]); }
      if (user) {
        const usersRes = await api.get('/api/users').catch(() => ({data:{data:[]}}));
        setEndUsers((usersRes.data.data || []).filter(u => u.role === 'enduser'));
        try {
          const sr = await api.get('/api/subscriptions');
          const m = {};
          (sr.data.data || []).forEach(x => { if (x.user_id && x.mac_address) { (m[x.user_id] = m[x.user_id] || new Set()).add(x.mac_address); } });
          const o = {};
          Object.keys(m).forEach(k => { o[k] = Array.from(m[k]).join(', '); });
          setViewerMacs(o);
        } catch { setViewerMacs({}); }
      }
    } catch { toast.error('Failed to load ads'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAds(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (editAd) {
        await api.put(`/api/ads/${editAd.id}`, toPayload(form));
        toast.success('Ad updated');
      } else {
        await api.post('/api/ads', toPayload(form));
        toast.success(user?.role === 'superadmin' ? 'Ad created and pushed to all tenants' : 'Ad created for your viewers');
      }
      setShowModal(false);
      setEditAd(null);
      setForm(emptyForm);
      fetchAds();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ad campaign?')) return;
    try { await api.delete(`/api/ads/${id}`); toast.success('Deleted'); fetchAds(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleTenantToggle = async (ad, enabled) => {
    try {
      await api.put(`/api/ads/${ad.id}/tenant-settings`, {
        enabled,
        schedule_start: ad.tenant_schedule_start || null,
        schedule_end: ad.tenant_schedule_end || null
      });
      toast.success(enabled ? 'Ad enabled for your viewers' : 'Ad disabled for your viewers');
      fetchAds();
    } catch { toast.error('Failed to update'); }
  };

  const openTargetModal = async (ad) => {
    setTargetAd(ad);
    setTargetMode(ad.tenant_target_mode || 'all');
    setTargetVideo({
      on_channel_change: (ad.tenant_video_on_change ?? ad.on_channel_change) === true,
      every_minutes: String(ad.tenant_video_every_minutes ?? ad.every_minutes ?? '') === '0' ? '' : String(ad.tenant_video_every_minutes ?? ad.every_minutes ?? ''),
      min_gap_minutes: String(ad.tenant_video_min_gap ?? ad.min_gap_minutes ?? 10)
    });
    try {
      const res = await api.get(`/api/ads/${ad.id}/viewer-targets`);
      setSelectedViewerIds(res.data.data || []);
    } catch { setSelectedViewerIds([]); }
    setShowTargetModal(true);
  };
  const saveTargets = async () => {
    try {
      await api.put(`/api/ads/${targetAd.id}/tenant-settings`, {
        enabled: targetAd.tenant_enabled !== false,
        schedule_start: targetAd.tenant_schedule_start || null,
        schedule_end: targetAd.tenant_schedule_end || null,
        target_mode: targetMode
      });
      if (targetMode === 'selected') {
        await api.put(`/api/ads/${targetAd.id}/viewer-targets`, { viewer_ids: selectedViewerIds });
      }
      if (targetAd.ad_type === 'video') {
        await api.put(`/api/ads/${targetAd.id}/video-frequency`, {
          on_channel_change: targetVideo.on_channel_change,
          every_minutes: targetVideo.every_minutes === '' ? 0 : targetVideo.every_minutes,
          min_gap_minutes: targetVideo.min_gap_minutes
        });
      }
      toast.success('Targeting saved');
      setShowTargetModal(false);
      fetchAds();
    } catch { toast.error('Failed to save targeting'); }
  };
  const isOwner = (ad) => ad.created_by === user?.id;

  if (loading) return <div className="loading"><div className="spinner" />Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title"><Megaphone size={22} style={{verticalAlign:'middle', marginRight:'8px'}} />Ads</h1>
          <div className="page-subtitle">
            {user?.role === 'superadmin'
              ? 'Ads you create here are pushed to all tenants, who may opt out individually.'
              : 'Create ads for your own viewers, or manage ads pushed to you by the platform.'}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditAd(null); setForm(emptyForm); setShowModal(true); }}>
          <PlusCircle size={16} /> Create Ad
        </button>
      </div>

      <div className="card">
        {ads.length === 0 ? (
          <div className="empty-state">No ads yet</div>
        ) : (
          <table>
            <thead><tr>
              <th>Name</th><th>Type</th><th>Scope</th><th>Status</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {ads.map(ad => (
                <tr key={ad.id}>
                  <td>{ad.name}</td>
                  <td>{ad.ad_type === 'video' ? '🎬 Video' : '📝 Text'}</td>
                  <td>
                    <span className={`badge ${ad.scope === 'global' ? 'badge-info' : 'badge-success'}`}>
                      {ad.scope === 'global' ? 'Global (all tenants)' : 'My viewers only'}
                    </span>
                    {ad.scope === 'global' && !isOwner(ad) && <div style={{fontSize:'11px', color:'var(--text-secondary)', marginTop:'4px'}}>Pushed by {ad.created_by_name}</div>}
                  </td>
                  <td>
                    {isOwner(ad) ? (
                      <span className={`badge ${ad.is_active ? 'badge-success' : 'badge-danger'}`}>{ad.is_active ? 'Active' : 'Disabled'}</span>
                    ) : (
                      <label style={{display:'flex', alignItems:'center', gap:'6px', cursor:'pointer'}}>
                        <input type="checkbox" checked={ad.tenant_enabled !== false} onChange={e => handleTenantToggle(ad, e.target.checked)} />
                        {ad.tenant_enabled !== false ? 'Enabled for you' : 'Disabled for you'}
                      </label>
                    )}
                  </td>
                  <td>
                    <div style={{display:'flex', gap:'6px'}}>
                      {isOwner(ad) && (
                        <>
                          <button className="btn btn-secondary" style={{padding:'4px 10px'}} onClick={() => {
                            setEditAd(ad);
                            setForm({
                              name: ad.name, ad_type: ad.ad_type, video_url: ad.video_url || '',
                              text_content: ad.text_content || '', display_style: ad.display_style || 'ticker',
                              schedule_start: toLocalInput(ad.schedule_start),
                              schedule_end: toLocalInput(ad.schedule_end),
                              is_active: ad.is_active,
                            channel_ids: ad.channel_ids || [],
                            target_mode: ad.target_mode || 'all',
                            viewer_ids: ad.viewer_ids || [],
                            side: ad.side || 'right', image_url: ad.image_url || '', bottom_image_url: ad.bottom_image_url || '', at_launch: ad.at_launch !== false,
                            on_channel_change: !!ad.on_channel_change, every_minutes: ad.every_minutes ? String(ad.every_minutes) : '',
                            min_gap_minutes: String(ad.min_gap_minutes || 10)
                            });
                            setShowModal(true);
                          }}><Edit size={14} /></button>
                          <button className="btn btn-danger" style={{padding:'4px 10px'}} onClick={() => handleDelete(ad.id)}><Trash2 size={14} /></button>
                        </>
                      )}
                      {user?.role !== 'superadmin' && (
                        <button className="btn btn-secondary" style={{padding:'4px 10px', fontSize:'12px'}} onClick={() => openTargetModal(ad)}>
                          🎯 {ad.tenant_target_mode === 'selected' ? 'Targeted' : 'Target'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editAd ? 'Edit Ad' : 'Create Ad'}{user?.role === 'superadmin' ? ' (Global)' : ' (My Viewers Only)'}</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Ad Name *</label>
                  <input className="form-control" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                </div>
                {!editAd && (
                  <div className="form-group">
                    <label>Ad Type *</label>
                    <select className="form-control" value={form.ad_type} onChange={e => setForm({...form, ad_type: e.target.value})}>
                      <option value="text">Text or picture (ticker, banner, L-shape)</option>
                      <option value="video">Short Video</option>
                    </select>
                  </div>
                )}
                {form.ad_type === 'video' ? (
                  <div className="form-group">
                    <label>Video URL *</label>
                    <input className="form-control" placeholder="https://... or choose a file below" value={form.video_url} onChange={e => setForm({...form, video_url: e.target.value})} required />
                    <input type="file" accept="video/mp4,video/webm" style={{ marginTop: '6px' }} onChange={e => { uploadMedia('video', e.target.files[0], 'video_url'); e.target.value = ''; }} />
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{uploading || 'MP4 (H.264 video, AAC audio), up to 40 seconds and 50 MB.'}</div>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label>{form.display_style === 'lshape' ? 'Text (optional for L-shape)' : 'Text Content *'}</label>
                      <input className="form-control" placeholder="Your advertisement message..." value={form.text_content} onChange={e => setForm({...form, text_content: e.target.value})} required={form.display_style !== 'lshape'} />
                    </div>
                    <div className="form-group">
                      <label>Display Style</label>
                      <select className="form-control" value={form.display_style} onChange={e => setForm({...form, display_style: e.target.value})}>
                        <option value="ticker">Scrolling Ticker</option>
                        <option value="banner">Corner Banner</option>
                        <option value="lshape">L-shape (video shrinks, ad frames the screen)</option>
                      </select>
                    </div>
                  </>
                )}
                <div className="form-group">
                  {form.ad_type === 'text' && (
                  <>
                    {form.display_style === 'lshape' && (
                      <div style={{ marginBottom: '10px' }}>
                        <label>L-shape: which side?</label>
                        <select className="form-control" value={form.side} onChange={e => setForm({ ...form, side: e.target.value })}>
                          <option value="right">Ad on the right side and bottom</option>
                          <option value="left">Ad on the left side and bottom</option>
                        </select>
                        <label style={{ marginTop: '8px' }}>Side panel picture (tall, 480 x 810 recommended)</label>
                        <input className="form-control" placeholder="https://example.com/ad.png or choose a file below" value={form.image_url}
                          onChange={e => setForm({ ...form, image_url: e.target.value })} />
                        <input type="file" accept="image/png,image/jpeg,image/webp" style={{ marginTop: '6px' }} onChange={e => { uploadMedia('image', e.target.files[0], 'image_url'); e.target.value = ''; }} />
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{uploading || 'PNG, JPG or WebP, up to 5 MB.'}</div>
                        <label style={{ marginTop: '10px' }}>Bottom strip picture (wide, 1920 x 270 recommended)</label>
                        <input className="form-control" placeholder="https://... or choose a file below" value={form.bottom_image_url}
                          onChange={e => setForm({ ...form, bottom_image_url: e.target.value })} />
                        <input type="file" accept="image/png,image/jpeg,image/webp" style={{ marginTop: '6px' }} onChange={e => { uploadMedia('image', e.target.files[0], 'bottom_image_url'); e.target.value = ''; }} />
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>PNG, JPG or WebP, up to 5 MB. Text above is optional for this style.</div>
                      </div>
                    )}
                    <label>Show on channels (optional)</label>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                      {form.channel_ids.length === 0
                        ? 'Showing on every channel. Tick channels below to limit this ad to only those.'
                        : `Showing only on ${form.channel_ids.length} selected channel(s).`}
                      {form.channel_ids.length > 0 && (
                        <button type="button" className="btn btn-secondary" style={{ marginLeft: '8px', padding: '2px 8px', fontSize: '11px' }}
                          onClick={() => setForm({ ...form, channel_ids: [] })}>Clear</button>
                      )}
                    </div>
                    <input className="form-control" placeholder="Search channels..." value={chSearch}
                      onChange={e => setChSearch(e.target.value)} style={{ marginBottom: '6px' }} />
                    <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px' }}>
                      {channels
                        .filter(c => !chSearch || (c.name || '').toLowerCase().includes(chSearch.toLowerCase()))
                        .slice(0, 200)
                        .map(c => (
                          <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', cursor: 'pointer' }}>
                            <input type="checkbox" checked={form.channel_ids.includes(c.id)}
                              onChange={e => setForm({ ...form, channel_ids: e.target.checked ? [...form.channel_ids, c.id] : form.channel_ids.filter(id => id !== c.id) })} />
                            {c.lcn ? c.lcn + ' - ' : ''}{c.name}
                          </label>
                        ))}
                    </div>
                  </>
                )}
              </div>
              <div className="form-group">
                {(() => {
                  const term = vSearch.toLowerCase();
                  const shown = endUsers.filter(u => !term ||
                    (u.full_name || '').toLowerCase().includes(term) ||
                    (u.username || '').toLowerCase().includes(term) ||
                    (u.phone || '').toLowerCase().includes(term) ||
                    (viewerMacs[u.id] || '').toLowerCase().includes(term)).slice(0, 200);
                  return (
                    <>
                      <label>Who should see this ad?</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <input type="radio" checked={form.target_mode !== 'selected'} onChange={() => setForm({ ...form, target_mode: 'all' })} />
                        {user?.role === 'superadmin' ? 'Every viewer on the platform' : 'All my viewers'}
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <input type="radio" checked={form.target_mode === 'selected'} onChange={() => setForm({ ...form, target_mode: 'selected' })} />
                        Only the viewers I pick
                      </label>
                      {form.target_mode === 'selected' && (
                        <>
                          <input className="form-control" placeholder="Search by name, phone, username or device ID..." value={vSearch}
                            onChange={e => setVSearch(e.target.value)} style={{ marginBottom: '6px' }} />
                          <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px' }}>
                            {shown.length === 0 ? (
                              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '6px' }}>No viewers found</div>
                            ) : shown.map(u => (
                              <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', cursor: 'pointer' }}>
                                <input type="checkbox" checked={form.viewer_ids.includes(u.id)}
                                  onChange={e => setForm({ ...form, viewer_ids: e.target.checked ? [...form.viewer_ids, u.id] : form.viewer_ids.filter(id => id !== u.id) })} />
                                {u.full_name || u.username}{u.phone ? ' - ' + u.phone : ''}{viewerMacs[u.id] ? ' [' + viewerMacs[u.id] + ']' : ''}{user?.role === 'superadmin' && u.parent_username ? ' (under ' + u.parent_username + ')' : ''}
                              </label>
                            ))}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{form.viewer_ids.length} viewer(s) selected</div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="form-group">
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  Leave both boxes empty to show the ad all the time until you turn it off. If you set a start or end time, it uses your own device clock and time zone, and the ad stops once the end time passes.
                </div>
                {form.ad_type === 'video' && (
                  <div style={{ marginBottom: '12px', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    <label>When should this video play?</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <input type="checkbox" checked={form.at_launch} onChange={e => setForm({ ...form, at_launch: e.target.checked })} /> When the app opens
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <input type="checkbox" checked={form.on_channel_change} onChange={e => setForm({ ...form, on_channel_change: e.target.checked })} /> When a viewer changes channel
                    </label>
                    <label>But no more than once every (minutes)</label>
                    <input className="form-control" type="number" min="1" value={form.min_gap_minutes} onChange={e => setForm({ ...form, min_gap_minutes: e.target.value })} style={{ marginBottom: '6px' }} />
                    <label>Also while watching, every (minutes, empty = never)</label>
                    <input className="form-control" type="number" min="1" value={form.every_minutes} onChange={e => setForm({ ...form, every_minutes: e.target.value })} />
                  </div>
                )}
                <label>Schedule Start (optional)</label>
                  <input className="form-control" type="datetime-local" value={form.schedule_start} onChange={e => setForm({...form, schedule_start: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Schedule End (optional)</label>
                  <input className="form-control" type="datetime-local" value={form.schedule_end} onChange={e => setForm({...form, schedule_end: e.target.value})} />
                </div>
                {editAd && (
                  <div className="form-group">
                    <label style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} />
                      Active
                    </label>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editAd ? 'Save Changes' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showTargetModal && targetAd && (
        <div className="modal-overlay" onClick={() => setShowTargetModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Target Viewers — {targetAd.name}</div>
              <button className="modal-close" onClick={() => setShowTargetModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px'}}>
                  <input type="radio" checked={targetMode === 'all'} onChange={() => setTargetMode('all')} />
                  All my viewers
                </label>
                <label style={{display:'flex', alignItems:'center', gap:'8px'}}>
                  <input type="radio" checked={targetMode === 'selected'} onChange={() => setTargetMode('selected')} />
                  Specific viewers only
                </label>
              </div>
              {targetAd.ad_type === 'video' && (
                <div className="form-group" style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '12px' }}>
                  <label>Video timing for your viewers</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <input type="checkbox" checked={targetVideo.on_channel_change} onChange={e => setTargetVideo({ ...targetVideo, on_channel_change: e.target.checked })} /> Play when a viewer changes channel
                  </label>
                  <label>No more than once every (minutes)</label>
                  <input className="form-control" type="number" min="1" value={targetVideo.min_gap_minutes} onChange={e => setTargetVideo({ ...targetVideo, min_gap_minutes: e.target.value })} style={{ marginBottom: '6px' }} />
                  <label>Also while watching, every (minutes, empty = never)</label>
                  <input className="form-control" type="number" min="1" value={targetVideo.every_minutes} onChange={e => setTargetVideo({ ...targetVideo, every_minutes: e.target.value })} />
                </div>
              )}
              {targetMode === 'selected' && (
                <div className="form-group">
                  <label>Select Viewers ({selectedViewerIds.length} selected)</label>
                  <div style={{maxHeight:'280px', overflowY:'auto', border:'1px solid var(--border)', borderRadius:'8px', padding:'8px'}}>
                    {endUsers.length === 0 ? (
                      <div style={{color:'var(--text-secondary)', fontSize:'13px', padding:'8px'}}>No viewers found</div>
                    ) : endUsers.map(u => (
                      <label key={u.id} style={{display:'flex', alignItems:'center', gap:'8px', padding:'6px 0', cursor:'pointer'}}>
                        <input type="checkbox" checked={selectedViewerIds.includes(u.id)}
                          onChange={e => {
                            if (e.target.checked) setSelectedViewerIds([...selectedViewerIds, u.id]);
                            else setSelectedViewerIds(selectedViewerIds.filter(id => id !== u.id));
                          }} />
                        {u.full_name || u.username} {u.phone ? `— ${u.phone}` : ''}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTargetModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveTargets}>Save Targeting</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ads;
